import { AsyncButton, Button, Modal } from '@atrvd/saas-ui'

/**
 * Props for the ConfirmationDialog component
 */
interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Dialog title */
  title: string
  /** Dialog description/message */
  description: string
  /** Text for confirm button */
  confirmText?: string
  /** Text for cancel button */
  cancelText?: string
  /** Callback when confirm is clicked */
  onConfirm: () => void
  /** Loading state for confirm button */
  isLoading?: boolean
  /** Variant for confirm button */
  variant?: 'default' | 'destructive'
}

/**
 * Reusable confirmation dialog for destructive or important actions.
 *
 * Title, message and actions all render inside the Modal BODY — we deliberately
 * do NOT pass Modal's `title`/`footer` props. Those would each draw their own
 * divider (header `border-b`, footer `border-t`) around the otherwise-empty
 * content area, leaving two stray divider lines with a blank band between them.
 * Keeping everything in one body block gives a clean, compact confirm card with
 * no dividers. `ariaLabel` preserves the accessible name Modal's header `title`
 * would otherwise provide.
 */
export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  isLoading = false,
  variant = 'default',
}: ConfirmationDialogProps) {
  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      ariaLabel={title}
      size="sm"
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <AsyncButton
            variant={variant}
            onClick={onConfirm}
            isLoading={isLoading}
            loadingText={confirmText}
          >
            {confirmText}
          </AsyncButton>
        </div>
      </div>
    </Modal>
  )
}
